import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatUsdt } from '../../utils/format';
import { TOTAL_PACKAGES, PACKAGE_PRICES } from '../../config/contracts';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';

// Shows total available balance = pending royalty + held bucket (auto-upgrade fund).
// Both amounts belong to the user — royalty is claimable, held bucket releases via auto-upgrade.
const AvailableClaim = ({ userId, heldBucket, heldTarget, topPackageId, onClaimed }) => {
  const { logic, storage } = useContracts();
  const notify = useNotify();

  const [rows, setRows] = useState([]);
  const [claimedByPkg, setClaimedByPkg] = useState({}); // pkgId => BigNumber sum of historical RoyaltyClaimed
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const runIdRef = useRef(0);

  const load = async () => {
    if (!userId) return;
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      // Royalty eligibility rule:
      //   directsInPackage[user][1] >= 5
      //   AND for pkg < 12: topPackageId >= pkg + 1
      //       for pkg 12 (max tier): topPackageId == 12 (must be at top rank)
      // The pkg-1 direct count is the SAME for all royalty tiers — fetch it once.
      // Loops 1..TOTAL_PACKAGES (12) so max-rank users get to see P12 royalty too.
      const [pkg1Directs, ...rest] = await Promise.all([
        storage.directsInPackage(userId, 1).catch(() => ethers.BigNumber.from(0)),
        ...Array.from({ length: TOTAL_PACKAGES }, (_, i) => Promise.all([
          logic.getPendingRoyalty(userId, i + 1).catch(() => ethers.BigNumber.from(0)),
          storage.royaltyEligibility(i + 1, userId).catch(() => false)
        ]))
      ]);
      if (myRun !== runIdRef.current) return;

      const directsNum = pkg1Directs.toNumber ? pkg1Directs.toNumber() : Number(pkg1Directs);
      const needsMore = Math.max(0, 5 - directsNum);

      const list = rest.map(([pending, eligible], idx) => ({
        pkgId: idx + 1,
        pending,
        eligible,
        directs: directsNum,
        needsMore
      }));

      setRows(list);
    } catch (err) {
      console.error('AvailableClaim load error:', err);
    } finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [userId]);

  const totalPending = rows.reduce((s, r) => s.add(r.pending), ethers.BigNumber.from(0));
  const bucket = heldBucket || ethers.BigNumber.from(0);
  const totalAvailable = totalPending.add(bucket);

  const claimableRows = rows.filter((r) => !r.pending.isZero());
  const eligibleCount = rows.filter((r) => r.eligible).length;

  // Nice per-tier "what's needed" hint
  const nextTierLabel = (pkgId) => {
    if (pkgId === TOTAL_PACKAGES) return `Need to be on rank P${TOTAL_PACKAGES}`;
    return `Need to activate P${pkgId + 1}`;
  };

  // Held bucket status
  const targetPkg = heldTarget || 0;
  const cap = targetPkg > 0 ? ethers.utils.parseUnits(String(PACKAGE_PRICES[targetPkg - 1]), 18) : ethers.BigNumber.from(0);
  const bucketReady = targetPkg > 0 && bucket.gte(cap);
  const bucketPercent = targetPkg > 0 && !cap.isZero()
    ? Math.min(100, bucket.mul(10000).div(cap).toNumber() / 100)
    : 0;

  const claimOne = async (pkgId) => {
    setClaiming(pkgId);
    const loadingId = notify.loading(`Claiming from Package ${pkgId}...`);
    try {
      const tx = await logic.claimRoyalty(pkgId);
      notify.dismiss(loadingId);
      const mining = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(mining);
      notify.success('Royalty claimed successfully');
      await load();
      if (onClaimed) onClaimed();
    } catch (err) {
      notify.dismiss(loadingId);
      notify.error(parseError(err));
    } finally {
      setClaiming(null);
    }
  };

  const claimAll = async () => {
    for (const r of claimableRows) {
      await claimOne(r.pkgId);
    }
  };

  const triggerUpgrade = async () => {
    setUpgrading(true);
    const l = notify.loading('Triggering auto-upgrade...');
    try {
      const tx = await logic.triggerAutoUpgrade();
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success('Auto-upgraded — leftover released to wallet');
      if (onClaimed) onClaimed();
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally {
      setUpgrading(false);
    }
  };

  const displayRows = showAll
    ? rows
    : rows.filter((r) => r.eligible || !r.pending.isZero());

  return (
    <Card
      title="Available Balance"
      subtitle="Your total unrealized income"
      glow
    >
      {/* Combined balance hero */}
      <div className="claim-hero">
        <div className="claim-hero-left">
          <div className="claim-hero-label">
            <span className="pulse-dot"></span>
            Total Available
          </div>
          <div className="claim-hero-value">
            {loading ? '...' : formatUsdt(totalAvailable)}
            <span className="denom"> USDT</span>
          </div>
          <div className="claim-hero-sub">
            Royalty + Hold For Upgrade
          </div>
        </div>

        {claimableRows.length > 1 && (
          <button
            className="btn btn-primary btn-lg claim-all-btn"
            onClick={claimAll}
            disabled={claiming !== null}
          >
            {claiming !== null ? <><MiniLoader /> Claiming</> : `Claim All Royalty`}
          </button>
        )}
      </div>

      {/* Split: royalty pending + held bucket */}
      <div className="balance-split">
        <div className={`balance-tile ${!totalPending.isZero() ? 'active' : ''}`}>
          <div className="balance-tile-icon" style={{ background: 'linear-gradient(135deg,#EC4899,#BE185D)' }}>👑</div>
          <div className="balance-tile-info">
            <div className="balance-tile-label">Pending Royalty</div>
            <div className="balance-tile-value">
              {formatUsdt(totalPending)} <span className="bt-unit">USDT</span>
            </div>
            <div className="balance-tile-sub">
              {claimableRows.length > 0 ? `In ${claimableRows.length} pkg${claimableRows.length > 1 ? 's' : ''} — claim below` : 'Claimable per package'}
            </div>
          </div>
        </div>

        <div className={`balance-tile ${!bucket.isZero() ? 'active' : ''}`}>
          <div className="balance-tile-icon" style={{ background: 'linear-gradient(135deg,#7C3AED,#22D3EE)' }}>⏸</div>
          <div className="balance-tile-info">
            <div className="balance-tile-label">Hold For Upgrade</div>
            <div className="balance-tile-value">
              {formatUsdt(bucket)} <span className="bt-unit">USDT</span>
            </div>
            <div className="balance-tile-sub">
              {targetPkg > 0
                ? bucketReady ? `Ready — Auto-Upgrade to P${targetPkg}` : `Releases when you activate P${targetPkg}`
                : 'Max tier reached'}
            </div>
          </div>
        </div>
      </div>

      {/* Bucket action: if ready, one-click auto-upgrade */}
      {bucketReady && (
        <button
          className="btn btn-primary btn-full mt-md"
          onClick={triggerUpgrade}
          disabled={upgrading}
          style={{ padding: '14px 20px', fontSize: 15 }}
        >
          {upgrading ? <><MiniLoader /> Upgrading</> : `⚡ Auto-Upgrade to Package ${targetPkg} & Release Extra`}
        </button>
      )}

      {targetPkg > 0 && !bucket.isZero() && !bucketReady && (
        <div className="bucket-note">
          💡 Your bucket fills automatically from income earned on your top package.
          Two ways to unlock these funds:
          <div style={{ marginTop: 8, paddingLeft: 4 }}>
            <div>⚡ <strong>Auto-Upgrade</strong> — once bucket ≥ ${PACKAGE_PRICES[targetPkg - 1]},
              it buys Package {targetPkg} and releases any surplus to your wallet.</div>
            <div style={{ marginTop: 4 }}>💳 <strong>Manual activation</strong> — pay to activate
              Package {targetPkg} yourself and the entire held bucket releases to your wallet.</div>
          </div>
        </div>
      )}

      {/* Royalty per-package rows */}
      <div className="claim-section-heading">
        Royalty by Package
        <span className="claim-section-sub">{eligibleCount} eligible</span>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-center" style={{ padding: 20 }}><MiniLoader /></div>
      )}

      {!loading && rows.length > 0 && (
        <>
          <div className="royalty-list">
            {displayRows.map((r) => (
              <RoyaltyRow
                key={r.pkgId}
                row={r}
                onClaim={() => claimOne(r.pkgId)}
                claiming={claiming === r.pkgId}
                disabled={claiming !== null}
              />
            ))}
          </div>

          {displayRows.length < rows.length && !showAll && (
            <button
              className="btn btn-secondary btn-sm btn-full mt-md"
              onClick={() => setShowAll(true)}
            >
              Show all {rows.length} royalty packages ›
            </button>
          )}
          {showAll && (
            <button
              className="btn btn-secondary btn-sm btn-full mt-md"
              onClick={() => setShowAll(false)}
            >
              Hide inactive packages
            </button>
          )}
        </>
      )}
    </Card>
  );
};

const RoyaltyRow = ({ row, onClaim, claiming, disabled }) => {
  const { pkgId, pending, eligible, needsMore } = row;
  const hasClaim = !pending.isZero();
  const isMaxPkg = pkgId === TOTAL_PACKAGES;

  const notEligibleLabel = needsMore > 0
    ? `Need ${needsMore} more P1 direct${needsMore !== 1 ? 's' : ''}`
    : isMaxPkg
      ? `Need to reach rank P${TOTAL_PACKAGES}`
      : `Need to activate P${pkgId + 1}`;

  return (
    <div className={`royalty-row ${hasClaim ? 'has-claim' : ''} ${!eligible && !hasClaim ? 'not-eligible' : ''}`}>
      <div className="royalty-row-left">
        <div className="royalty-pkg-badge">P{pkgId}</div>
        <div>
          <div className="royalty-row-title">
            Package {pkgId}
            {isMaxPkg && <span className="badge badge-aurora" style={{ marginLeft: 6, fontSize: 9 }}>MAX</span>}
          </div>
          <div className="royalty-row-meta">
            ${PACKAGE_PRICES[pkgId - 1]}
            {eligible ? (
              <span className="badge badge-success" style={{ marginLeft: 6, fontSize: 9 }}>Eligible</span>
            ) : (
              <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 9 }}>
                {notEligibleLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="royalty-row-right">
        <div className="royalty-amount">
          <div className={hasClaim ? 'royalty-amount-value active' : 'royalty-amount-value'}>
            {formatUsdt(pending)}
          </div>
          <div className="royalty-amount-unit">USDT</div>
        </div>
        <button
          className={hasClaim ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
          onClick={onClaim}
          disabled={disabled || !hasClaim}
        >
          {claiming ? <MiniLoader /> : 'Claim'}
        </button>
      </div>
    </div>
  );
};

export default AvailableClaim;
